import {
  Card as MuiCard,
  CardMedia,
  CardContent,
  Typography,
  Link,
  Box,
} from '@mui/material';
import React, { ReactElement, useMemo } from 'react';
import { useRecoilValue } from 'recoil';

import { SmaverseTag } from '@app/components/Product/SmaverseTag';
import { attributesBySmaverseSelector } from '@app/domain/catalog';
import { useDeviceType } from '@app/hooks/useBrowserHooks';
import { useGADataLayer } from '@app/hooks/useGADataLayer';
import { GA_CUSTOM_EVENT } from '@app/static/constants';
import { theme } from '@app/theme';
import { Attachment, Attribute, ProductRangeTypeKey } from '@app/types/catalog';
import { checkSmaverseSupport } from '@app/utils/catalog';
import { formatPrice, formatPriceRange } from '@app/utils/format';

interface CardProps {
  address: string;
  imageSrc: string;
  isProperty?: boolean;
  maxPrice?: number;
  minPrice?: number;
  organizationId?: string;
  priceRangeType?: ProductRangeTypeKey;
  productAttributes?: Attribute[];
  productId?: string;
  title: string;
}

export function ExhibitionCard({
  imageSrc,
  title,
  address,
  organizationId,
  productId,
  productAttributes,
}: CardProps): ReactElement {
  const { isMobile } = useDeviceType();
  const smaverseAttributes = useRecoilValue(attributesBySmaverseSelector);
  const isSmaverse = useMemo(() => {
    return checkSmaverseSupport(productAttributes || [], smaverseAttributes);
  }, [productAttributes, smaverseAttributes]);
  const { sendGAEvent } = useGADataLayer();
  const handleClick = () => {
    sendGAEvent(GA_CUSTOM_EVENT.COMPANY_VIEW_EXHIBITION, isMobile);
  };
  return (
    <Link
      href={
        productId ? `/exhibitions/${productId}` : `/companies/${organizationId}`
      }
      underline="none"
      onClick={handleClick}
      width="100%"
      sx={{ minWidth: { md: 'unset', xs: 300 } }}
    >
      <MuiCard
        sx={{
          '&:hover .overlay': {
            opacity: 1,
          },
          bgcolor: '#f5f8fc',
          borderRadius: 0,
          boxShadow: 3,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <Box
          className="overlay"
          sx={{
            bgcolor: 'rgba(255, 255, 255, 0.4)',
            height: '100%',
            left: 0,
            opacity: 0,
            position: 'absolute',
            top: 0,
            transition: 'opacity 0.3s ease',
            width: '100%',
            zIndex: 1,
          }}
        />

        <CardContent sx={{ p: 0 }}>
          {isSmaverse && (
            <SmaverseTag
              sx={{
                left: {
                  md: '30px',
                  xs: '6px',
                },
                top: '22px',
              }}
            />
          )}
          <Box
            sx={{
              backgroundImage: `url(${imageSrc})`,
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: '110%',
              height: '200px',
              mx: 'auto',
              width: '100%',
            }}
          />
          <Box
            p={2}
            sx={{
              height: '70px',
              mx: 'auto',
            }}
          >
            <Typography
              variant="subtitle2"
              component="div"
              color={theme.palette.blueAccent.main}
              fontWeight="bold"
            >
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {address}
            </Typography>
          </Box>
        </CardContent>
      </MuiCard>
    </Link>
  );
}

export function PropertyCard({
  imageSrc,
  title,
  address,
  minPrice,
  productId,
  maxPrice,
  priceRangeType,
}: CardProps): ReactElement {
  const { isMobile } = useDeviceType();
  const { sendGAEvent } = useGADataLayer();
  const handleClick = () => {
    sendGAEvent(GA_CUSTOM_EVENT.COMPANY_VIEW_PROPERTIES, isMobile);
  };

  return (
    <Link
      href={`/properties/${productId}`}
      underline="none"
      onClick={handleClick}
      sx={{ minWidth: { md: 'unset', xs: 300 } }}
      width="100%"
    >
      <MuiCard
        sx={{
          '&:hover .overlay': {
            opacity: 1,
          },
          bgcolor: 'white',
          borderRadius: 0,
          boxShadow: 3,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        <Box
          className="overlay"
          sx={{
            bgcolor: 'rgba(255, 255, 255, 0.4)',
            height: '100%',
            left: 0,
            opacity: 0,
            position: 'absolute',
            top: 0,
            transition: 'opacity 0.3s ease',
            width: '100%',
            zIndex: 1,
          }}
        />
        <CardContent sx={{ p: 0 }}>
          <Box
            sx={{
              backgroundImage: `url(${imageSrc})`,
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: '110%',
              height: '200px',
              mx: 'auto',
              width: '100%',
            }}
          />
          <Box
            px={2}
            sx={{
              mx: 'auto',
              paddingTop: '16px',
              width: '100%',
            }}
          >
            <Typography
              variant="subtitle2"
              component="div"
              fontWeight="bold"
              color="primary"
            >
              {title}
            </Typography>
            <Typography variant="subtitle2" component="div" fontWeight="bold">
              {formatPriceRange(
                minPrice ? Number(minPrice) : undefined,
                maxPrice ? Number(maxPrice) : undefined,
                formatPrice,
                priceRangeType
              )}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {address}
            </Typography>
          </Box>
        </CardContent>
      </MuiCard>
    </Link>
  );
}

interface InquiryPropertyCardProps {
  access?: string;
  imageSrc: Attachment[];
  isProperty?: boolean;
  maxPrice?: number;
  minPrice?: number;
  name: string;
  priceRangeType?: ProductRangeTypeKey;
}

export function InquiryPropertyCard({
  imageSrc,
  name,
  access,
  minPrice,
  maxPrice,
  priceRangeType,
}: InquiryPropertyCardProps): ReactElement {
  const imageUrl = imageSrc && imageSrc.length > 0 ? imageSrc[0].url : '';
  const imageAlt =
    imageSrc && imageSrc.length > 0 ? imageSrc[0].type : '物件画像';

  return (
    <MuiCard
      sx={{
        border: `1px solid ${theme.palette.neutral.silver}`,
        borderRadius: 0,
        boxShadow: 3,
        display: 'flex',
        flexDirection: { md: 'row', xs: 'column' },
        p: 3,
        width: '100%',
      }}
    >
      <CardMedia
        component="img"
        image={imageUrl}
        alt={imageAlt}
        sx={{ m: 2, maxHeight: 340, width: { md: 250, xs: 'unset' } }}
      />
      <CardContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
        }}
      >
        <Typography variant="subtitle2" fontWeight="bold">
          {name}
        </Typography>
        <Typography>
          {formatPriceRange(
            minPrice ? Number(minPrice) : undefined,
            maxPrice ? Number(maxPrice) : undefined,
            formatPrice,
            priceRangeType
          )}
        </Typography>
        <Typography color="text.secondary">{access}</Typography>
      </CardContent>
    </MuiCard>
  );
}
